import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, ButtonProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dropdown from '../Dropdown';

const List = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 8,
});

type PaginationButtonType = 'previous' | 'next' | 'page';

interface PaginationButtonProps extends ButtonProps {
  selected?: boolean;
  btnType?: PaginationButtonType;
}

const PaginationButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'btnType',
})<PaginationButtonProps>(({ theme, selected, disabled, btnType }) => ({
  minWidth: 28,
  height: 28,
  padding: '0px 10px',
  borderRadius: theme.borderRadius.lg,
  fontSize: '12px',
  fontWeight: 500,
  cursor: disabled ? 'not-allowed' : 'pointer',
  backgroundColor: selected ? theme.palette.paginationSelectedBg : theme.palette.background.paper,
  color: disabled
    ? theme.palette.paginationDisabledText
    : selected
      ? theme.palette.common.white
      : theme.palette.text.primary,
  border:
    btnType === 'previous' || btnType === 'next' || selected
      ? 'none'
      : `1px solid ${theme.palette.paginationBorder}`,
  '&:hover': {
    backgroundColor: selected
      ? theme.palette.paginationSelectedHoverBg
      : theme.palette.background.paper,
  },
}));

const Ellipsis = styled('span')(({ theme }) => ({
  color: theme.palette.paginationEllipsis,
  fontSize: 14,
  padding: '0 8px',
}));

interface CustomPaginationProps {
  page: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (_args: { page: number; pageSize: number }) => void;
  onPageSizeChange: (_args: { pageSize: number }) => void;
  isPageSizeVisible?: boolean;
  totalRecords?: number;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  page,
  pageCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isPageSizeVisible = true,
  totalRecords = 0,
}) => {
  const { t } = useTranslation();
  const { items } = usePagination({
    count: pageCount,
    page: page + 1,
    siblingCount: 1,
    onChange: (_e: React.ChangeEvent<unknown>, value: number) =>
      onPageChange({ page: value - 1, pageSize }),
  });

  const pageSizeOptions = [10, 25, 50, 100];

  return (
    <Stack
      sx={{ paddingBottom: 0, paddingTop: 2, width: '100%' }}
      direction="row"
      justifyContent={isPageSizeVisible ? 'space-between' : 'flex-end'}
      alignItems="center"
    >
      {isPageSizeVisible && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '50%' }}>
          <Typography
            sx={{
              width: '70px',
              fontSize: '14px',
              minWidth: '60px',
            }}
          >
            {t('table.pageSize')}
          </Typography>
          <Dropdown
            noSort={true}
            hasSearch={false}
            sx={{ width: '70px' }}
            height={32}
            selectContainerSx={{ height: '35px !important', minHeight: '35px !important' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange({ pageSize: e.target.value })}
            options={pageSizeOptions.map((i) => ({ label: i.toString(), value: i }))}
          />
          <Typography sx={{ ml: 2, flex: 1, fontSize: '14px' }}>
            {t('table.showingEntries', {
              from: pageSize * page + 1,
              to: pageSize * (page + 1) > totalRecords ? totalRecords : pageSize * (page + 1),
              total: totalRecords,
            })}
          </Typography>
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <nav>
          <List>
            {items.map(
              ({ page: itemPage, type, selected, disabled, ...item }: any, index: number) => {
                if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                  return (
                    <li key={index}>
                      <Ellipsis>…</Ellipsis>
                    </li>
                  );
                }

                return (
                  <li key={index}>
                    <PaginationButton
                      type={type === 'previous' || type === 'next' ? 'button' : 'button'}
                      selected={selected}
                      disabled={disabled}
                      btnType={
                        (type === 'previous' || type === 'next'
                          ? type
                          : 'page') as PaginationButtonType
                      }
                      {...(item as any)}
                      sx={{
                        fontSize: '11px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textTransform: 'capitalize',
                      }}
                      startIcon={
                        type === 'previous' && (
                          <ArrowBackIcon sx={{ fontSize: '15px !important' }} />
                        )
                      }
                      endIcon={
                        type === 'next' && <ArrowForwardIcon sx={{ fontSize: '15px !important' }} />
                      }
                      disableElevation
                      disableRipple
                    >
                      {type === 'previous'
                        ? t('common.previous')
                        : type === 'next'
                          ? t('common.next')
                          : itemPage}
                    </PaginationButton>
                  </li>
                );
              },
            )}
          </List>
        </nav>
      </Box>
    </Stack>
  );
};

export default CustomPagination;
